import { mergeAttributes, Node } from "@tiptap/core"

import {
	clampIconSize,
	iconAttributes,
	lucideIconName,
	sanitizeIconColor,
} from "./icon-html"

export interface IconNodeAttributes {
	icon: string
	size?: number | null
	color?: string | null
}

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		icon: {
			insertIcon: (attributes: IconNodeAttributes) => ReturnType
			/** Changes the selected icon. */
			updateIcon: (attributes: Partial<IconNodeAttributes>) => ReturnType
		}
	}
}

export const IconNode = Node.create({
	name: "icon",
	group: "inline",
	inline: true,
	atom: true,
	selectable: true,
	draggable: true,

	addAttributes() {
		return {
			icon: {
				default: null,
				parseHTML: (element: HTMLElement) =>
					lucideIconName(element.getAttribute("data-icon")),
				rendered: false,
			},
			size: {
				default: null,
				parseHTML: (element: HTMLElement) =>
					clampIconSize(element.getAttribute("data-size")),
				rendered: false,
			},
			color: {
				default: null,
				parseHTML: (element: HTMLElement) =>
					sanitizeIconColor(element.getAttribute("data-color")),
				rendered: false,
			},
		}
	},

	parseHTML() {
		return [{ tag: 'span[data-type="icon"]' }]
	},

	renderHTML({ node, HTMLAttributes }) {
		const attributes = iconAttributes(node.attrs.icon, {
			size: node.attrs.size as number | null,
			color: node.attrs.color as string | null,
		})
		// An unusable name keeps its place in the document but shows nothing.
		const rendered = attributes
			? { ...attributes }
			: { "data-type": "icon", class: "pl-icon", "aria-hidden": "true" }
		return ["span", mergeAttributes(HTMLAttributes, rendered), ["wbr"]]
	},

	renderText() {
		return ""
	},

	addCommands() {
		return {
			insertIcon:
				(attributes) =>
				({ commands }) => {
					const icon = lucideIconName(attributes.icon)
					if (!icon) return false
					return commands.insertContent({
						type: this.name,
						attrs: {
							icon,
							size: clampIconSize(attributes.size),
							color: sanitizeIconColor(attributes.color),
						},
					})
				},

			updateIcon:
				(changes) =>
				({ commands, state }) => {
					const selected = state.doc.nodeAt(state.selection.from)
					if (selected?.type.name !== this.name) return false
					const next = { ...selected.attrs, ...changes }
					const icon = lucideIconName(next.icon)
					if (!icon) return false
					return commands.updateAttributes(this.name, {
						icon,
						size: clampIconSize(next.size),
						color: sanitizeIconColor(next.color),
					})
				},
		}
	},
})

export const IconExtensions = [IconNode]
